import { useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtom, atom } from 'jotai';
import { ReactTable, Tooltip } from '@crazyegginc/hatch';
import classNames from 'classnames';

import {
  useSearchParams,
  useModal,
  usePermissions,
  useNeedsUpgradeToAccessFeature,
  useSite,
  useQueryParams,
  useDebounce,
} from '/src/hooks';
import { generateRecordingsFilterUrlForGoal } from '/src/features/goals/utils';
import { getParam } from '/src/utils/location';
import { getQueryParams } from '/src/utils/url';
import { isSemrush } from '/src/utils/semrush';

// ICONS & ILLUSTRATIONS
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as RecordingIcon } from '@crazyegginc/hatch/dist/images/icon-recording-filled.svg';
import { ReactComponent as ABTestIcon } from '@crazyegginc/hatch/dist/images/icon-ab-test-outline.svg';

// COMPONENTS
import { SEO } from '/src/components/SEO';
import { SitePickerBar } from '/src/components/site-picker';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { GoalsDashHeader } from '/src/features/goals/components/dashboard/DashHeader';
import { GoalTriggersTooltip } from '/src/features/goals/components/GoalTriggersTooltip';
import { NoGoalsWall } from '../paywalls/NoGoalsWall';
import { AtomSearchInput } from '/src/components/search-input';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { DashboardPage } from '/src/components/Page';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { RecordingsUpgradeModal } from '/src/features/recordings/components/dashboard/modals/RecordingsUpgradeModal';
import { ABTestingUpgradeModal } from '/src/features/ab-testing/components/modals/ABTestingUpgradeModal';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DateDisplay } from '/src/components/date-time/DateDisplay';
import { CreatedByText } from '/src/components/CreatedByText';

import { goalListSummaryQuery } from '/src/features/goals/queries';

import { FEATURES, SORT_ORDER_TYPES } from '/src/features/_global/constants';

import { camelToSnake, snakeToCamel } from '/src/utils/string';
import { AppLink } from '/src/features/_global/components/AppLink';
import {
  ManageSitesButton,
  SourceSelector,
  ViewAllSitesButton,
  PickerActionButton,
} from '/src/components/source-selector/source-selector';

const filterAtom = atom({ search: getParam('search') ?? '' });

function GoalsActions() {
  return (
    <div className="mb-[15px] flex w-full justify-between">
      <div className="ml-auto">
        <AtomSearchInput atom={filterAtom} placeholder="Search for Conversion" />
      </div>
    </div>
  );
}

function GoalsTable({ goals, fetching, hasNextPage, loadNextPage }) {
  const [searchParams] = useSearchParams();
  const { setAll: setAllParams } = useQueryParams();
  const { sites, selectedSite } = useSite({ dontSelectSite: true });
  const permissions = usePermissions();
  const canViewGoals = permissions.can('view', FEATURES.GOALS).allowed;
  const needsUpgradeToAccessRecordings = useNeedsUpgradeToAccessFeature(FEATURES.RECORDINGS);
  const needsUpgradeToAccessABTesting = useNeedsUpgradeToAccessFeature(FEATURES.AB_TESTING);
  const modal = useModal();

  const getSiteName = useCallback(
    (cellRowSiteId) => {
      if (!selectedSite?.name) {
        return sites.find((x) => cellRowSiteId === x.id)?.name ?? null;
      }

      return selectedSite?.name;
    },
    [selectedSite?.name, sites],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Conversion name',
        accessorKey: 'name',
        size: 340,
        meta: {
          align: 'center',
          justify: 'left',
          padding: '0px 8px',
        },
        cell: function FingerprintCell({ row }) {
          const haveTriggers = row?.original?.triggers ?? false;

          return (
            <div className="flex min-w-0 pr-5">
              <div className="flex w-full items-center">
                <div className="flex flex-1 items-center overflow-hidden">
                  {canViewGoals ? (
                    <AppLink to={`/goals/${row.original.id}`} className="text-link truncate">
                      {row.original.name}
                    </AppLink>
                  ) : (
                    <span className="text-link truncate">{row.original.name}</span>
                  )}
                </div>
                {haveTriggers && row?.original?.triggers.length > 0 && (
                  <div className="leading-[0px]">
                    <GoalTriggersTooltip triggers={row?.original?.triggers} className="ml-2.5">
                      <InfoIcon className="h-4 w-4 fill-current text-dodger-blue-300" aria-label="Trigger list" />
                    </GoalTriggersTooltip>
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        size: 90,
        meta: {
          align: 'center',
          justify: 'left',
        },
        cell: function GoalsCreatedAtCell({ row }) {
          return (
            <div className={classNames('flex w-full flex-col items-start', { 'h-[40px] justify-center': isSemrush })}>
              <DateDisplay date={row.original.createdAt} />
              <CreatedByText name={row?.original?.createdBy?.name ?? row.original.createdBy.login} />
            </div>
          );
        },
      },
      {
        header: function GoalsConversionsHeader() {
          return (
            <div className="flex items-center">
              <div className="mr-1.5 leading-none text-right">Recent Conversions</div>
              <Tooltip
                tooltipContent={
                  <div className="w-[335px] px-2 py-2.5">
                    <h5 className="mb-5 text-xs font-bold text-white">
                      The number of conversions in the last 7 days, including today.
                    </h5>
                    <h5 className="mb-2.5 text-xs font-bold text-white">
                      Does this look different to your A/B test conversions?
                    </h5>
                    <p className="text-xs font-light leading-tight tracking-wide text-white">
                      Conversions for A/B tests are only counted if a visitor has landed on your test pages{' '}
                      <strong>before</strong> triggering a conversion event, whereas all conversion events are counted
                      in your Conversion dashboard.
                    </p>
                  </div>
                }
              >
                <InfoIcon
                  className="h-3 w-3 -translate-y-px transform fill-current text-dodger-blue-300"
                  aria-label="Info on conversion"
                />
              </Tooltip>
            </div>
          );
        },
        accessorKey: 'conversions',
        size: 110,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: function GoalsConversionsCell({ row }) {
          return (row?.original?.recentConversionsCount ?? 0).toLocaleString();
        },
      },
      {
        header: 'Recordings',
        size: 80,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: ({ row }) => {
          return needsUpgradeToAccessRecordings ? (
            <button type="button" onClick={() => modal.show(<RecordingsUpgradeModal />)}>
              <RecordingIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to recordings related to ${row.original.name}`}
              />
            </button>
          ) : (
            <AppLink
              to={{
                pathname: '/recordings',
                search: getQueryParams({
                  filters: generateRecordingsFilterUrlForGoal(row.original),
                  site: getSiteName(row.original.siteId),
                }),
              }}
              className="flex items-center"
            >
              <span className="mr-2">{(row?.original?.sessionsCount ?? 0).toLocaleString()}</span>
              <RecordingIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to recordings related to ${row.original.name}`}
              />
            </AppLink>
          );
        },
      },
      {
        header: 'a/b test',
        accessorKey: 'abTests',
        size: 80,
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: ({ row }) => {
          return needsUpgradeToAccessABTesting ? (
            <button type="button" className="mr-2 " onClick={() => modal.show(<ABTestingUpgradeModal />)}>
              <ABTestIcon
                className="h-4 w-4 fill-current text-malibu-500"
                aria-label={`link to A/B Tests related to ${row.original.name}`}
              />
            </button>
          ) : (
            <>
              {row?.original?.abTestsCountActive.toLocaleString() || 0}
              <AppLink
                to={{
                  pathname: `/goals/${row.original.id}`,
                  hash: 'ab-tests',
                }}
                className="ml-2 flex items-center"
              >
                <ABTestIcon
                  className="h-4 w-4 fill-current text-malibu-500"
                  aria-label={`link to A/B Tests related to ${row.original.name}`}
                />
              </AppLink>
            </>
          );
        },
      },
    ],
    [canViewGoals, getSiteName, modal, needsUpgradeToAccessRecordings, needsUpgradeToAccessABTesting],
  );

  const tableRef = useRef(null);

  const tableSortData = useMemo(() => {
    return {
      id: snakeToCamel(searchParams.get('order') ?? 'CREATED_AT'),
      desc: (searchParams.get('sort') ?? SORT_ORDER_TYPES.DESC) === SORT_ORDER_TYPES.DESC,
    };
  }, [searchParams]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setAllParams({
          order: camelToSnake(sorting[0].id).toUpperCase(),
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setAllParams],
  );

  return (
    <ReactTable
      columns={columns}
      data={goals}
      fetching={fetching}
      ref={{ tableRef }}
      enableSorting={true}
      sorting={tableSortData}
      onFetchData={onFetchData}
      rowHeight={90}
      dynamicRowHeight={true}
      hasNextPage={hasNextPage}
      isNextPageLoading={fetching}
      loadNextPage={loadNextPage}
      useVirtualization={true}
      rowPadding={true}
      rowCustomClassname={() => 'py-3.75 pr-10'}
      headerCustomClassname="!pr-10"
    />
  );
}

function GoalsDashContent() {
  const [searchParams] = useSearchParams();
  const { set: setParam } = useQueryParams();
  const permissions = usePermissions();
  const { sites, loadingSites, selectedSite, reloadSites } = useSite({ enableAllSites: true });

  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const [filter, setFilter] = useAtom(filterAtom);
  const debouncedSearchValue = useDebounce(filter.search, 500);

  useEffect(() => {
    setParam('search', debouncedSearchValue);
  }, [debouncedSearchValue, setParam]);

  const navigate = useNavigate();
  const {
    data: goalsData,
    error,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...goalListSummaryQuery({
      siteId: selectedSite?.id ?? undefined,
      search: debouncedSearchValue && debouncedSearchValue.length > 0 ? debouncedSearchValue : undefined,
      order: searchParams.get('order'),
      sort: searchParams.get('sort'),
    }),
    enabled: Boolean(selectedSite),
  });

  const isLoading = !goalsData && !error;

  const goals = useMemo(
    () => (goalsData?.pages ?? []).reduce((acc, page) => [...acc, ...(page?.goalListSummary?.list ?? [])], []) ?? [],
    [goalsData],
  );

  if (!loadingSites && !sites.length && canEditSites) {
    return (
      <AddSiteModal
        customTitle="Add a site before creating Goals"
        onExtraSteps={() => {
          reloadSites();
        }}
        onCancel={() => navigate('/snapshots', { replace: true })}
      />
    );
  }

  if (!isLoading && goals.length === 0 && !debouncedSearchValue) {
    return <NoGoalsWall />;
  }

  return (
    <>
      <InstallationBanner />
      <div className="mt-10 flex w-full flex-col px-10">
        <GoalsActions />
        <div className="flex w-full justify-center">
          {!isLoading && goals.length === 0 && debouncedSearchValue?.length > 0 ? (
            <NoMatchesWall text="matches for this search" reset={() => setFilter({ search: '' })} actionText="search" />
          ) : (
            <GoalsTable goals={goals} fetching={isLoading} hasNextPage={hasNextPage} loadNextPage={fetchNextPage} />
          )}
        </div>
      </div>
    </>
  );
}

// eslint-disable-next-line no-restricted-syntax
export default function GoalsDashboard() {
  const { sites, loadingSites, selectedSite, selectSite } = useSite({ enableAllSites: true });
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.GOALS);
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.GOALS} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title="Conversions" />
      <GoalsDashHeader />
      <SitePickerBar
        Picker={SourceSelector}
        pickerClass="absolute top-full left-0"
        actionButtons={[
          <PickerActionButton key="picker_btn_ga" onClick={() => {}}>
            Google Analytics Settings
          </PickerActionButton>,
          <PickerActionButton key="picker_btn_sites" onClick={() => {}}>
            Enable Other Sites
          </PickerActionButton>,
          <ViewAllSitesButton key="picker_btn_all_sites" />,
          <ManageSitesButton key="picker_btn_manage_sites" />,
        ]}
        sites={sites}
        loading={loadingSites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        enableAllSites={true}
        addSiteOption={canEditSites}
        pageDisplayName={`Conversions dashboard`}
      />
      <PaymentAlerts />
      <GoalsDashContent />
    </DashboardPage>
  );
}
